import React, { FunctionComponent, useState } from 'react';
import { Box, Button, Divider, LinearProgress, Tooltip, Typography, makeStyles } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Container, Panel } from '@michelin/acid-components';
import { useSnackbar, useTranslation } from '@michelin/central-provider';
import { PageTitle } from 'components/PageTitle/PageTitle';
import { useExtractMileage, useUploadMileage } from 'hooks/useMileageDeclarations';
import { usePresignedUpload } from 'hooks/usePresignedUpload';
import { useParams } from 'react-router-dom';
import { FileUploadZone } from './Components/FileUploadZone';
import { MileageDataTable } from './Components/MileageDataTable';

const useStyles = makeStyles((theme) => ({
  header: {
    padding: theme.spacing(3),
    paddingBottom: 0,
  },
  content: {
    padding: theme.spacing(3),
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  alertMargin: {
    marginBottom: 16,
  },
  errorSection: {
    marginBottom: 24,
  },
}));

interface viewParams {
  selectedAccount: string;
  profileId: string;
}

export const MileageDeclarations: FunctionComponent = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const params = useParams<viewParams>();
  const classes = useStyles();
  const fleetId = params.selectedAccount;

  const [file, setFile] = useState<File | null>(null);
  const [fileKey, setFileKey] = useState(0);
  const [fileError, setFileError] = useState('');
  const [extractDisabled, setExtractDisabled] = useState(false);

  const getPresignedURL = usePresignedUpload({ fleetId });
  const mileageExtraction = useExtractMileage({ fleetId });
  const mileageUpload = useUploadMileage({ fleetId });

  const EXTRACTION_ERROR_DISPLAY_COUNT = 10;
  const VALID_FILE_TYPES = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
  const VALID_FILE_EXTENSIONS = ['.xlsx'];

  const canExtract =
    !!file &&
    !!getPresignedURL.uploadedFileId &&
    !getPresignedURL.isLoading &&
    !mileageExtraction.isLoading &&
    !extractDisabled;

  const extractTooltip = (() => {
    if (!file) return t('Please upload a file before extracting');
    if (extractDisabled) return t('Please select the file again to extract');
    return '';
  })();

  const isValidExcelFile = (file: File) => {
    return (
      VALID_FILE_TYPES.includes(file.type) || VALID_FILE_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext))
    );
  };

  const clearExtractState = () => {
    mileageExtraction.reset();
    setExtractDisabled(false);
  };

  const resetAllState = () => {
    setFile(null);
    setFileKey((prev) => prev + 1);
    setFileError('');
    clearExtractState();
    mileageUpload.reset();
  };

  const handleFileSelection = async (selectedFile: File | null) => {
    setFileError('');
    clearExtractState();
    getPresignedURL.reset();

    if (!selectedFile) {
      setFile(null);
      return;
    }

    if (!isValidExcelFile(selectedFile)) {
      setFile(null);
      setFileError(t('Only Excel files (.xlsx) are allowed'));
      return;
    }

    setFile(selectedFile);
    const id = await getPresignedURL.startUpload(selectedFile);
    if (!id && getPresignedURL.error) setFileError(getPresignedURL.error);
  };

  const handleUploadExtract = async () => {
    if (!canExtract || !getPresignedURL.uploadedFileId) return;

    const response = await mileageExtraction.mutateAsync(getPresignedURL.uploadedFileId);

    if (response && response.data?.length > 0) {
      enqueueSnackbar(t('File successfully extracted. Showing mileage declaration data below.'), {
        variant: 'success',
      });
    }

    setExtractDisabled(true);
  };

  const performUpload = async () => {
    if (!mileageExtraction.data || !getPresignedURL.uploadedFileId) return;

    const result = await mileageUpload.mutateAsync(getPresignedURL.uploadedFileId);

    if (result) {
      enqueueSnackbar(t('Successfully uploaded mileage declaration'), { variant: 'success' });
      resetAllState();
    }
  };

  const handleSubmit = async () => {
    if (!mileageExtraction.data) return;
    await performUpload();
  };

  const hasValidTable = !!(mileageExtraction.data && mileageExtraction.data.invalidRows === 0);
  return (
    <>
      <PageTitle title={t('Mileage Declarations')} />
      <Container>
        <Panel spacing={0}>
          <Box className={classes.header}>
            <Typography variant="h5">{t('Upload Mileage Declarations')}</Typography>
          </Box>
          <Divider />
          <Box className={classes.content}>
            <FileUploadZone file={file} fileKey={fileKey} fileError={fileError} onFileSelected={handleFileSelection} />
            {(getPresignedURL.isLoading || mileageExtraction.isLoading) && (
              <LinearProgress style={{ marginBottom: 16 }} />
            )}
            {(getPresignedURL.error || mileageExtraction.error) && (
              <Alert severity="error" className={classes.alertMargin}>
                {getPresignedURL.error || mileageExtraction.error}
              </Alert>
            )}
            <Box className={classes.actionButtons}>
              <Tooltip title={extractTooltip} arrow placement="top">
                <span>
                  <Button variant="contained" color="primary" disabled={!canExtract} onClick={handleUploadExtract}>
                    {getPresignedURL.isLoading || mileageExtraction.isLoading ? t('Loading...') : t('Extract')}
                  </Button>
                </span>
              </Tooltip>
            </Box>
            {mileageExtraction.data && mileageExtraction.data.validation.totalIssues > 0 && (
              <Box className={classes.errorSection}>
                <Typography variant="h6" style={{ marginBottom: 6 }}>
                  {t('Please correct the following errors in your file:')}
                </Typography>
                <Alert severity="error" style={{ marginBottom: 8 }}>
                  {mileageExtraction.data.validation.issues.slice(0, EXTRACTION_ERROR_DISPLAY_COUNT).map((err, idx) => (
                    <div key={idx}>{`${t('Row {{rowNumber}}: {{issue}}', {
                      rowNumber: err.rowNumber,
                      issue: err.issue,
                    })}`}</div>
                  ))}
                </Alert>
                <Typography variant="caption" color="textSecondary">
                  {t('Showing {{shown}} of {{total}} errors', {
                    shown: Math.min(EXTRACTION_ERROR_DISPLAY_COUNT, mileageExtraction.data.validation.issues.length),
                    total: mileageExtraction.data.validation.totalIssues,
                  })}
                </Typography>
              </Box>
            )}
            {hasValidTable && (
              <>
                <Typography variant="h6" style={{ marginBottom: 16 }}>
                  {t('Submitting Declarations for {{period}}', {
                    period: mileageExtraction.data!.summary.period + ' ' + mileageExtraction.data!.summary.year,
                  })}
                </Typography>
                <MileageDataTable data={mileageExtraction.data!.data} />
                {mileageUpload.isLoading && <LinearProgress style={{ marginTop: 16, marginBottom: 16 }} />}
                <Box display="flex" justifyContent="center" mt={2}>
                  <Button variant="contained" color="primary" disabled={mileageUpload.isLoading} onClick={handleSubmit}>
                    {mileageUpload.isLoading ? t('Loading...') : t('Submit Declarations')}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Panel>
      </Container>
    </>
  );
};
